<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Leads</h5>
        <p class="page-sub">Track and qualify potential customers</p>
      </div>
      <div class="header-actions">
        <div class="export-wrap" ref="exportWrap">
          <button class="btn-export" @click="exportMenuOpen = !exportMenuOpen" :disabled="exporting">
            <font-awesome-icon icon="download" /> Export <font-awesome-icon icon="chevron-down" style="font-size:10px" />
          </button>
          <div v-if="exportMenuOpen" class="export-menu">
            <button class="export-opt" @click="openExportModal('csv')"><font-awesome-icon icon="file-csv" class="me-2" />Export CSV</button>
            <button class="export-opt" @click="openExportModal('pdf')"><font-awesome-icon icon="file-pdf" class="me-2" />Export PDF</button>
          </div>
        </div>
        <button v-if="permission?.create_leads" class="btn-primary" @click="openCreate">
          <font-awesome-icon icon="plus" class="me-1" /> New Lead
        </button>
      </div>
    </div>

    <div class="filter-bar">
      <div class="per-page-wrap">
        <label class="per-page-label">Show</label>
        <select class="per-page-select" v-model.number="length" @change="page = 1; fetchLeads()">
          <option v-for="n in [5,10,20,50,100,200,500,1000]" :key="n" :value="n">{{ n }}</option>
        </select>
        <span class="per-page-label">per page</span>
      </div>
      <input v-model="search" @input="debouncedFetch" class="filter-input" placeholder="Search name, email, company..." />
      <select v-model="filterStatus" @change="page=1; fetchLeads()" class="filter-select">
        <option value="">All Statuses</option>
        <option value="new">New</option>
        <option value="contacted">Contacted</option>
        <option value="qualified">Qualified</option>
        <option value="unqualified">Unqualified</option>
        <option value="converted">Converted</option>
        <option value="lost">Lost</option>
      </select>
      <select v-model="filterSource" @change="page=1; fetchLeads()" class="filter-select">
        <option value="">All Sources</option>
        <option value="website">Website</option>
        <option value="referral">Referral</option>
        <option value="social_media">Social Media</option>
        <option value="cold_call">Cold Call</option>
        <option value="email_campaign">Email Campaign</option>
        <option value="event">Event</option>
        <option value="other">Other</option>
      </select>
      <input type="date" v-model="dateFrom" class="filter-input" style="max-width:160px" />
      <input type="date" v-model="dateTo" class="filter-input" style="max-width:160px" />
      <button class="btn-apply" @click="applyDateFilter">Apply</button>
      <button class="btn-clear" @click="clearDateFilter" v-if="dateFrom || dateTo">Clear</button>
    </div>

    <div class="card">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div><p>Loading leads...</p>
      </div>
      <div v-else-if="!leads.length" class="empty-state">
        <font-awesome-icon icon="user-plus" class="empty-icon" />
        <p>No leads found</p>
        <span>Add your first lead to start tracking prospects</span>
      </div>
      <table v-else class="data-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Company</th>
            <th>Source</th>
            <th>Status</th>
            <th>Created</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="l in leads" :key="l.id">
            <td class="name-col">{{ l.full_name || (l.first_name + ' ' + l.last_name).trim() || '—' }}</td>
            <td>{{ l.email ?? '—' }}</td>
            <td>{{ l.phone ?? '—' }}</td>
            <td>{{ l.company_name ?? '—' }}</td>
            <td>{{ l.source ?? '—' }}</td>
            <td><span :class="['status-badge', `status-badge--${l.status}`]">{{ l.status }}</span></td>
            <td>{{ fmtDate(l.created_at) }}</td>
            <td>
              <div class="row-actions">
                <button v-if="permission?.edit_leads" class="btn-link btn-link--edit" @click="openEdit(l)">Edit</button>
                <button v-if="permission?.convert_leads && l.status !== 'converted'" class="btn-link btn-link--convert" @click="convertLead(l)">Convert</button>
                <button v-if="permission?.delete_leads" class="btn-link btn-link--del" @click="confirmDelete(l)">Delete</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="meta" class="pagination-bar">
        <span class="pagination-info">{{ meta.from ?? 1 }}–{{ meta.to ?? meta.total }} of {{ meta.total }} records</span>
        <div class="pagination-controls">
          <button class="pg-btn" :disabled="meta.current_page <= 1" @click="changePage(1)">«</button>
          <button class="pg-btn" :disabled="meta.current_page <= 1" @click="changePage(meta.current_page - 1)">‹</button>
          <div class="pg-pages">
            <button v-for="p in pageRange" :key="p" class="pg-num" :class="{ 'pg-num--active': p === meta.current_page }" @click="changePage(p)">{{ p }}</button>
          </div>
          <button class="pg-btn" :disabled="meta.current_page >= meta.last_page" @click="changePage(meta.current_page + 1)">›</button>
          <button class="pg-btn" :disabled="meta.current_page >= meta.last_page" @click="changePage(meta.last_page)">»</button>
        </div>
      </div>
    </div>

    <!-- Create / Edit Modal -->
    <div class="modal-overlay" :class="{ open: modalOpen }" @click.self="closeModal">
      <div class="modal-box">
        <div class="modal-header">
          <span class="modal-title">{{ editing ? 'Edit Lead' : 'New Lead' }}</span>
          <button class="modal-close" @click="closeModal"><font-awesome-icon icon="times" /></button>
        </div>
        <form @submit.prevent="submit">
          <div class="modal-body">
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">First Name <span class="req">*</span></label>
                <input v-model="form.first_name" class="form-input" required />
              </div>
              <div class="form-group">
                <label class="form-label">Last Name <span class="req">*</span></label>
                <input v-model="form.last_name" class="form-input" required />
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Email</label>
                <input v-model="form.email" type="email" class="form-input" />
              </div>
              <div class="form-group">
                <label class="form-label">Phone</label>
                <input v-model="form.phone" class="form-input" />
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Company</label>
                <input v-model="form.company_name" class="form-input" />
              </div>
              <div class="form-group">
                <label class="form-label">Job Title</label>
                <input v-model="form.job_title" class="form-input" />
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Business Location</label>
                <select v-model="form.location_id" class="form-input">
                  <option value="">— None —</option>
                  <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.name }}</option>
                </select>
              </div>
              <div class="form-group">
                <label class="form-label">Source</label>
                <select v-model="form.source" class="form-input">
                  <option value="">— Select —</option>
                  <option value="website">Website</option>
                  <option value="referral">Referral</option>
                  <option value="social_media">Social Media</option>
                  <option value="cold_call">Cold Call</option>
                  <option value="email_campaign">Email Campaign</option>
                  <option value="event">Event</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div class="form-row" v-if="editing">
              <div class="form-group form-group--full">
                <label class="form-label">Status</label>
                <select v-model="form.status" class="form-input">
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="qualified">Qualified</option>
                  <option value="unqualified">Unqualified</option>
                  <option value="lost">Lost</option>
                </select>
              </div>
            </div>
            <div class="form-group form-group--full">
              <label class="form-label">Notes</label>
              <textarea v-model="form.notes" class="form-input" rows="3"></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn-ghost" @click="closeModal">Cancel</button>
            <button type="submit" class="btn-primary" :disabled="submitting">
              {{ submitting ? 'Saving…' : (editing ? 'Save Changes' : 'Create Lead') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Confirm -->
    <div class="modal-overlay" :class="{ open: deleteTarget }" @click.self="deleteTarget = null">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <span class="modal-title">Delete Lead</span>
          <button class="modal-close" @click="deleteTarget = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <p>Delete <strong>{{ deleteTarget?.full_name }}</strong>? This cannot be undone.</p>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="deleteTarget = null">Cancel</button>
          <button class="btn-danger" @click="doDelete" :disabled="submitting">{{ submitting ? 'Deleting…' : 'Delete' }}</button>
        </div>
      </div>
    </div>

    <!-- ── Export Column Picker ── -->
    <transition name="fade">
      <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal = false">
        <div class="col-picker-modal">
          <div class="col-picker-header">
            <div>
              <h3 class="col-picker-title">Select Columns</h3>
              <p class="col-picker-sub">Choose what to include in your {{ pendingExportFmt.toUpperCase() }} export</p>
            </div>
            <button class="col-picker-close" @click="showExportModal = false"><font-awesome-icon icon="times" /></button>
          </div>
          <div class="col-picker-actions">
            <button class="col-picker-link" @click="selectedExportCols = exportColumnDefs.map(c => c.key)">Select all</button>
            <span class="col-picker-sep">·</span>
            <button class="col-picker-link" @click="selectedExportCols = []">Deselect all</button>
            <span class="col-picker-count">{{ selectedExportCols.length }} of {{ exportColumnDefs.length }} selected</span>
          </div>
          <div class="col-picker-grid">
            <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
              <input type="checkbox" :value="col.key" v-model="selectedExportCols" class="col-picker-check" />
              <span class="col-picker-label">{{ col.label }}</span>
            </label>
          </div>
          <div class="col-picker-footer">
            <button class="btn-ghost" @click="showExportModal = false">Cancel</button>
            <button class="btn-primary" :disabled="selectedExportCols.length === 0 || exporting" @click="doExport()">
              <font-awesome-icon :icon="exporting ? 'spinner' : pendingExportFmt === 'pdf' ? 'file-pdf' : 'file-csv'" :spin="exporting" />
              Export {{ pendingExportFmt.toUpperCase() }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Convert Lead Form -->
    <div class="modal-overlay" :class="{ open: !!convertTarget }" @click.self="convertTarget = null">
      <div class="modal-box modal-box--convert">
        <div class="modal-header">
          <div class="modal-header-icon"><font-awesome-icon icon="exchange-alt" /></div>
          <div>
            <span class="modal-title">Convert Lead</span>
            <p class="modal-sub">Choose what to create from this lead</p>
          </div>
          <button class="modal-close" @click="convertTarget = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">

          <!-- Lead summary -->
          <div class="convert-card">
            <div class="convert-avatar">{{ convertTarget?.first_name?.[0] }}{{ convertTarget?.last_name?.[0] }}</div>
            <div class="convert-info">
              <p class="convert-name">{{ convertTarget?.full_name || ((convertTarget?.first_name ?? '') + ' ' + (convertTarget?.last_name ?? '')).trim() }}</p>
              <p class="convert-meta" v-if="convertTarget?.company_name">{{ convertTarget.company_name }}</p>
              <p class="convert-meta" v-if="convertTarget?.email">{{ convertTarget.email }}</p>
            </div>
          </div>

          <!-- Contact section (always on) -->
          <div class="convert-section convert-section--locked">
            <div class="convert-section-header">
              <div class="convert-section-check convert-section-check--on">
                <font-awesome-icon icon="check" />
              </div>
              <div>
                <p class="convert-section-title">Contact</p>
                <p class="convert-section-sub">Always created from lead details</p>
              </div>
            </div>
          </div>

          <!-- Account section -->
          <div class="convert-section" :class="{ 'convert-section--active': convertForm.create_account }">
            <div class="convert-section-header" @click="convertForm.create_account = !convertForm.create_account" style="cursor:pointer">
              <div class="convert-section-check" :class="{ 'convert-section-check--on': convertForm.create_account }">
                <font-awesome-icon icon="check" v-if="convertForm.create_account" />
              </div>
              <div>
                <p class="convert-section-title">Account <span class="convert-optional">(optional)</span></p>
                <p class="convert-section-sub">Create a customer account for this company</p>
              </div>
            </div>
            <div v-if="convertForm.create_account" class="convert-section-body">
              <div class="form-group">
                <label class="form-label">Account Name</label>
                <input v-model="convertForm.account_name" class="form-input" placeholder="Company name" />
              </div>
              <div class="form-group" v-if="categories.length">
                <label class="form-label">Category <span class="convert-optional">(optional)</span></label>
                <select v-model="convertForm.category_id" class="form-input">
                  <option value="">— None —</option>
                  <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
                </select>
              </div>
              <div class="form-group">
                <label class="form-label">Business Location <span class="convert-optional">(optional)</span></label>
                <select v-model="convertForm.location_id" class="form-input">
                  <option value="">— None —</option>
                  <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.name }}</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Opportunity section -->
          <div class="convert-section" :class="{ 'convert-section--active': convertForm.create_opportunity }">
            <div class="convert-section-header" @click="convertForm.create_opportunity = !convertForm.create_opportunity" style="cursor:pointer">
              <div class="convert-section-check" :class="{ 'convert-section-check--on': convertForm.create_opportunity }">
                <font-awesome-icon icon="check" v-if="convertForm.create_opportunity" />
              </div>
              <div>
                <p class="convert-section-title">Opportunity <span class="convert-optional">(optional)</span></p>
                <p class="convert-section-sub">Create a deal in the sales pipeline</p>
              </div>
            </div>
            <div v-if="convertForm.create_opportunity" class="convert-section-body">
              <div class="form-group">
                <label class="form-label">Opportunity Name</label>
                <input v-model="convertForm.opportunity_name" class="form-input" />
              </div>
              <div class="form-group">
                <label class="form-label">Estimated Value (₦)</label>
                <input v-model="convertForm.opportunity_value" type="number" min="0" class="form-input" placeholder="0" />
              </div>
              <div class="form-group" v-if="employees.length">
                <label class="form-label">Assigned To <span class="convert-optional">(optional)</span></label>
                <Multiselect
                  v-model="convertForm.opportunity_assigned_to"
                  :options="employeeOptions"
                  :searchable="true"
                  :close-on-select="true"
                  placeholder="Search employee..."
                  value-prop="value"
                  label="label"
                  track-by="value"
                  class="ms-convert"
                />
              </div>
            </div>
          </div>

        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="convertTarget = null">Cancel</button>
          <button class="btn-convert" @click="doConvert" :disabled="submitting">
            <font-awesome-icon icon="exchange-alt" class="me-1" />
            {{ submitting ? 'Converting…' : 'Convert Lead' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';
import Multiselect from '@vueform/multiselect';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

export default {
  components: { Multiselect },
  setup() { return { toast: useToast(), store: useStore() }; },
  computed: {
    permission() { return this.store.state.getCurrentUserPermission; },
    pageRange() {
      if (!this.meta) return [];
      const cur = this.meta.current_page, last = this.meta.last_page;
      const range = [];
      for (let i = Math.max(1, cur - 2); i <= Math.min(last, cur + 2); i++) range.push(i);
      return range;
    },
    employeeOptions() {
      return this.employees.map(e => ({ value: e.id, label: `${e.first_name} ${e.last_name}` }));
    },
  },
  data() {
    return {
      leads: [], meta: null, loading: false, submitting: false,
      search: '', filterStatus: '', filterSource: '', page: 1, length: 5,
      dateFrom: '', dateTo: '', exportMenuOpen: false, exporting: false,
      showExportModal: false, pendingExportFmt: 'csv', selectedExportCols: [],
      exportColumnDefs: [
        { key: 'name',         label: 'Name',    pdfWidth: 130 },
        { key: 'email',        label: 'Email',   pdfWidth: 150 },
        { key: 'phone',        label: 'Phone',   pdfWidth: 100 },
        { key: 'company_name', label: 'Company', pdfWidth: 130 },
        { key: 'source',       label: 'Source',  pdfWidth: 100 },
        { key: 'status',       label: 'Status',  pdfWidth: 80 },
        { key: 'created_at',   label: 'Created', pdfWidth: 71 },
      ],
      modalOpen: false, editing: null, deleteTarget: null, convertTarget: null,
      form: { first_name:'', last_name:'', email:'', phone:'', company_name:'', job_title:'', source:'', status:'new', notes:'', location_id:'' },
      convertForm: { create_account: false, account_name:'', category_id:'', location_id:'', create_opportunity: false, opportunity_name:'', opportunity_value:'', opportunity_assigned_to: null },
      categories: [],
      locations:  [],
      employees: [],
      debounceTimer: null,
    };
  },
  mounted() { this.fetchLeads(); this.fetchCategories(); this.fetchEmployees(); this.fetchLocations(); document.addEventListener('click', this.onOutsideClick); },
  beforeUnmount() { document.removeEventListener('click', this.onOutsideClick); },
  methods: {
    async fetchLeads() {
      this.loading = true;
      try {
        const { data } = await axios.get('/crm/leads', {
          params: { search: this.search, status: this.filterStatus, source: this.filterSource, from: this.dateFrom || undefined, to: this.dateTo || undefined, page: this.page, length: this.length }
        });
        this.leads = data.data?.data ?? data.data ?? data ?? [];
        this.meta  = data.data?.meta ?? data.meta ?? null;
      } catch { this.toast.error('Failed to load leads'); }
      finally { this.loading = false; }
    },
    debouncedFetch() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => { this.page = 1; this.fetchLeads(); }, 400);
    },
    changePage(p) { this.page = p; this.fetchLeads(); },
    applyDateFilter() { this.page = 1; this.fetchLeads(); },
    clearDateFilter() { this.dateFrom = ''; this.dateTo = ''; this.page = 1; this.fetchLeads(); },
    openExportModal(format) {
      this.exportMenuOpen     = false;
      this.pendingExportFmt   = format;
      this.selectedExportCols = this.exportColumnDefs.map(c => c.key);
      this.showExportModal    = true;
    },
    getRowValue(r, key) {
      switch (key) {
        case 'name':         return r.full_name || (`${r.first_name??''} ${r.last_name??''}`).trim() || '—';
        case 'email':        return r.email ?? '—';
        case 'phone':        return r.phone ?? '—';
        case 'company_name': return r.company_name ?? '—';
        case 'source':       return r.source ?? '—';
        case 'status':       return r.status ?? '—';
        case 'created_at':   return this.fmtDate(r.created_at);
        default:             return '—';
      }
    },
    async doExport() {
      if (!this.selectedExportCols.length) return;
      this.exporting = true; this.showExportModal = false;
      try {
        const params = { page: 1, per_page: 10000 };
        if (this.search)       params.search = this.search;
        if (this.filterStatus) params.status = this.filterStatus;
        if (this.filterSource) params.source = this.filterSource;
        if (this.dateFrom)     params.from   = this.dateFrom;
        if (this.dateTo)       params.to     = this.dateTo;
        const { data } = await axios.get('/crm/leads', { params });
        const rows = data.data?.data ?? data.data ?? data ?? [];
        const cols = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key));
        const headers = cols.map(c => c.label);
        const body    = rows.map(r => cols.map(c => this.getRowValue(r, c.key)));
        const date    = new Date().toISOString().slice(0, 10);
        if (this.pendingExportFmt === 'csv') {
          const csv  = [headers, ...body].map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
          const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
          const url  = URL.createObjectURL(blob);
          const a    = document.createElement('a');
          a.href = url; a.download = `leads-${date}.csv`; a.click(); URL.revokeObjectURL(url);
        } else {
          const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
          const usable = 761; const totalW = cols.reduce((s,c)=>s+c.pdfWidth,0); const scale = usable/totalW;
          const colStyles = {}; cols.forEach((c,i)=>{ colStyles[i]={cellWidth:c.pdfWidth*scale}; });
          doc.setFontSize(18); doc.setFont('helvetica','bold'); doc.setTextColor(26,43,69); doc.text('Leads',40,46);
          doc.setFontSize(9); doc.setFont('helvetica','normal'); doc.setTextColor(122,144,176);
          doc.text(`Exported ${new Date().toLocaleDateString('en-GB')}   ·   ${rows.length} record(s)`,40,62);
          doc.autoTable({ startY:78, head:[headers], body,
            styles:{fontSize:7.5,cellPadding:3.5,textColor:[26,43,69],overflow:'ellipsize'},
            headStyles:{fillColor:[0,74,173],textColor:255,fontStyle:'bold',fontSize:7.5},
            alternateRowStyles:{fillColor:[248,250,252]}, columnStyles:colStyles, tableWidth:usable, margin:{left:40,right:40},
            didDrawPage(d){ doc.setFontSize(8); doc.setTextColor(192,202,214); doc.text(`Page ${d.pageNumber}`,doc.internal.pageSize.width/2,doc.internal.pageSize.height-16,{align:'center'}); },
          });
          doc.save(`leads-${date}.pdf`);
        }
      } catch { this.toast?.error('Export failed. Please try again.'); }
      finally { this.exporting = false; }
    },
    onOutsideClick(e) {
      if (this.$refs.exportWrap && !this.$refs.exportWrap.contains(e.target)) this.exportMenuOpen = false;
    },
    openCreate() { this.editing = null; this.form = { first_name:'', last_name:'', email:'', phone:'', company_name:'', job_title:'', source:'', status:'new', notes:'', location_id:'' }; this.modalOpen = true; },
    openEdit(l) { this.editing = l.id; this.form = { ...l, location_id: l.location_id ?? '' }; this.modalOpen = true; },
    closeModal() { this.modalOpen = false; },
    confirmDelete(l) { this.deleteTarget = l; },
    async submit() {
      this.submitting = true;
      try {
        if (this.editing) {
          await axios.put(`/crm/leads/${this.editing}`, this.form);
          this.toast.success('Lead updated');
        } else {
          await axios.post('/crm/leads', this.form);
          this.toast.success('Lead created');
        }
        this.closeModal();
        this.fetchLeads();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to save'); }
      finally { this.submitting = false; }
    },
    async doDelete() {
      this.submitting = true;
      try {
        await axios.delete(`/crm/leads/${this.deleteTarget.id}`);
        this.toast.success('Lead deleted');
        this.deleteTarget = null;
        this.fetchLeads();
      } catch { this.toast.error('Failed to delete'); }
      finally { this.submitting = false; }
    },
    async fetchLocations() {
      try {
        const { data } = await axios.get('/admin/location/active');
        this.locations = data.data ?? [];
      } catch { /* silent */ }
    },
    async fetchCategories() {
      try {
        const { data } = await axios.get('/crm/account-categories');
        this.categories = data.data ?? [];
      } catch { /* silent */ }
    },
    async fetchEmployees() {
      try {
        const { data } = await axios.get('/payroll/employees/active');
        this.employees = data.data ?? [];
      } catch { /* silent */ }
    },
    convertLead(l) {
      this.convertTarget = l;
      this.convertForm = {
        create_account:           !!l.company_name,
        account_name:             l.company_name || l.full_name || `${l.first_name ?? ''} ${l.last_name ?? ''}`.trim(),
        category_id:              '',
        location_id:              l.location_id ?? '',
        create_opportunity:       true,
        opportunity_name:         `Opportunity — ${l.full_name || (l.first_name + ' ' + l.last_name).trim()}`,
        opportunity_value:        '',
        opportunity_assigned_to:  l.assigned_to ?? null,
      };
    },
    async doConvert() {
      this.submitting = true;
      try {
        const payload = {
          create_account:          this.convertForm.create_account,
          account_name:            this.convertForm.account_name || null,
          category_id:             this.convertForm.category_id || null,
          location_id:             this.convertForm.location_id || null,
          create_opportunity:      this.convertForm.create_opportunity,
          opportunity_name:        this.convertForm.opportunity_name || null,
          opportunity_value:       this.convertForm.opportunity_value || 0,
          opportunity_assigned_to: this.convertForm.opportunity_assigned_to || null,
        };
        await axios.post(`/crm/leads/${this.convertTarget.id}/convert`, payload);
        const name = this.convertTarget.full_name || (this.convertTarget.first_name + ' ' + this.convertTarget.last_name).trim();
        const parts = ['Contact'];
        if (payload.create_account)     parts.push('Account');
        if (payload.create_opportunity) parts.push('Opportunity');
        this.toast.success(`${name} converted → ${parts.join(', ')} created`);
        this.convertTarget = null;
        this.fetchLeads();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Conversion failed'); }
      finally { this.submitting = false; }
    },
    fmtDate(v) { return new Date(v).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }); },
  }
};
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }
.filter-bar  { display:flex; gap:10px; margin-bottom:16px; flex-wrap:wrap; }
.filter-input  { padding:9px 13px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; flex:1; min-width:200px; outline:none; }
.filter-input:focus { border-color:#004AAD; }
.filter-select { padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; }
.btn-primary { background:#004AAD; color:#fff; border:none; padding:9px 18px; border-radius:8px; font-size:13px; font-weight:600; cursor:pointer; display:inline-flex; align-items:center; gap:6px; }
.btn-primary:hover { background:#003b8e; }
.btn-primary:disabled { opacity:.55; cursor:not-allowed; }
.btn-ghost  { background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; padding:8px 16px; border-radius:8px; font-size:13px; font-weight:600; cursor:pointer; }
.btn-danger { background:#e03535; color:#fff; border:none; padding:8px 16px; border-radius:8px; font-size:13px; font-weight:600; cursor:pointer; }
.btn-danger:disabled { opacity:.55; cursor:not-allowed; }
.card { background:#fff; border-radius:12px; border:1.5px solid #e8edf5; overflow:hidden; }
.loading-state,.empty-state { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:56px 24px; color:#7a90b0; gap:8px; }
.empty-icon { font-size:36px; opacity:.4; }
.spinner { width:28px; height:28px; border:3px solid #e8edf5; border-top-color:#004AAD; border-radius:50%; animation:spin .7s linear infinite; }
@keyframes spin { to { transform:rotate(360deg); } }
.data-table { width:100%; border-collapse:collapse; font-size:13px; }
.data-table th { background:#f4f7fb; font-weight:600; color:#5a6a85; padding:11px 14px; text-align:left; white-space:nowrap; }
.data-table td { padding:11px 14px; border-top:1px solid #f0f4fa; color:#1a2b45; vertical-align:middle; }
.data-table tbody tr:hover { background:#f9fbff; }
.name-col { font-weight:600; }
.row-actions { display:flex; gap:8px; }
.btn-link { background:none; border:none; color:#004AAD; font-size:12.5px; cursor:pointer; padding:0; font-weight:600; }
.btn-link--edit { color:#004AAD; }
.btn-link--convert { color:#0f766e; }
.btn-link--del { color:#e03535; }
.status-badge { display:inline-block; font-size:11px; font-weight:700; border-radius:20px; padding:2px 9px; text-transform:capitalize; background:#f1f5ff; color:#004AAD; }
.status-badge--new        { background:#e0f2fe; color:#075985; }
.status-badge--contacted  { background:#ede9fe; color:#6d28d9; }
.status-badge--qualified  { background:#dcfce7; color:#166534; }
.status-badge--unqualified { background:#fef3c7; color:#92400e; }
.status-badge--converted  { background:#ccfbf1; color:#0f766e; }
.status-badge--lost       { background:#fee2e2; color:#991b1b; }
.per-page-wrap{display:flex;align-items:center;gap:8px;flex-shrink:0}
.per-page-label{font-size:13px;color:#5a6a85;white-space:nowrap}
.per-page-select{padding:7px 10px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;background:#fff;outline:none;cursor:pointer}
.per-page-select:focus{border-color:#004AAD}
.pagination-bar{display:flex;align-items:center;justify-content:space-between;padding:12px 16px;border-top:1px solid #f0f4fa;flex-wrap:wrap;gap:10px}
.pagination-info{font-size:12.5px;color:#7a90b0}
.pagination-controls{display:flex;align-items:center;gap:4px}
.pg-btn{padding:6px 10px;border:1.5px solid #dde5f0;border-radius:7px;background:#fff;color:#1a2b45;font-size:13px;cursor:pointer}
.pg-btn:disabled{opacity:.4;cursor:not-allowed}
.pg-pages{display:flex;gap:3px}
.pg-num{width:30px;height:30px;border:1.5px solid #dde5f0;border-radius:7px;background:#fff;color:#1a2b45;font-size:12.5px;cursor:pointer;display:flex;align-items:center;justify-content:center}
.pg-num:hover{border-color:#004AAD;color:#004AAD}
.pg-num--active{background:#004AAD;border-color:#004AAD;color:#fff}
.modal-overlay { position:fixed; inset:0; z-index:1050; display:flex; align-items:center; justify-content:center; background:rgba(15,25,50,.45); backdrop-filter:blur(3px); opacity:0; pointer-events:none; transition:opacity .2s; padding:16px; }
.modal-overlay.open { opacity:1; pointer-events:all; }
.modal-box { background:#fff; border-radius:14px; width:100%; max-width:600px; box-shadow:0 20px 60px rgba(0,0,0,.14); animation:slideUp .2s ease; max-height:90vh; overflow-y:auto; }
.modal-box--sm { max-width:420px; }
@keyframes slideUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
.modal-header { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #e4eaf2; }
.modal-title { font-size:16px; font-weight:700; color:#1a2b45; }
.modal-close { width:28px; height:28px; border-radius:7px; border:1px solid #dde5f0; background:transparent; cursor:pointer; font-size:14px; color:#7a90b0; display:flex; align-items:center; justify-content:center; }
.modal-body { padding:20px 22px; }
.modal-footer { display:flex; justify-content:flex-end; gap:10px; padding:16px 22px; border-top:1px solid #f0f4fa; }
.form-row { display:grid; grid-template-columns:1fr 1fr; gap:14px; margin-bottom:4px; }
.form-group { display:flex; flex-direction:column; gap:5px; margin-bottom:14px; }
.form-group--full { grid-column:1/-1; }
.form-label { font-size:12.5px; font-weight:600; color:#1a2b45; }
.form-input { padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; width:100%; font-family:inherit; }
.form-input:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.07); }
.req { color:#e03535; }
.me-1 { margin-right:4px; }
/* convert modal */
.modal-box--convert { max-width:500px; }
.modal-header-icon { width:36px; height:36px; border-radius:9px; background:#e0f7f4; color:#0f766e; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
.modal-sub { font-size:12px; color:#7a90b0; margin:2px 0 0; }
.convert-card { display:flex; align-items:center; gap:14px; background:#f4f7fb; border-radius:10px; padding:14px 16px; margin-bottom:16px; }
.convert-avatar { width:44px; height:44px; border-radius:50%; background:#004AAD; color:#fff; display:flex; align-items:center; justify-content:center; font-size:16px; font-weight:700; flex-shrink:0; text-transform:uppercase; }
.convert-info { flex:1; min-width:0; }
.convert-name { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.convert-meta { font-size:12.5px; color:#7a90b0; margin:0 0 1px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.convert-section { border:1.5px solid #e8edf5; border-radius:10px; overflow:hidden; margin-bottom:10px; transition:border-color .15s; }
.convert-section--locked { border-color:#dcfce7; background:#f0fdf4; }
.convert-section--active { border-color:#004AAD; }
.convert-section-header { display:flex; align-items:center; gap:12px; padding:12px 16px; }
.convert-section-check { width:22px; height:22px; border-radius:6px; border:2px solid #d1d5db; display:flex; align-items:center; justify-content:center; font-size:11px; flex-shrink:0; color:#fff; transition:all .15s; }
.convert-section-check--on { background:#004AAD; border-color:#004AAD; }
.convert-section--locked .convert-section-check--on { background:#16a34a; border-color:#16a34a; }
.convert-section-title { font-size:13.5px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
.convert-section-sub { font-size:12px; color:#7a90b0; margin:0; }
.convert-optional { font-weight:400; color:#a0aec0; font-size:12px; }
.convert-section-body { padding:0 16px 14px; display:flex; flex-direction:column; gap:10px; }
.btn-convert { background:#0f766e; color:#fff; border:none; padding:8px 18px; border-radius:8px; font-size:13px; font-weight:600; cursor:pointer; display:inline-flex; align-items:center; gap:6px; }
.btn-convert:hover { background:#0d6461; }
.btn-convert:disabled { opacity:.55; cursor:not-allowed; }
.header-actions{display:flex;align-items:center;gap:10px}
.export-wrap{position:relative}
.btn-export{background:#f4f7fb;color:#3d5068;border:1.5px solid #e4eaf2;padding:9px 14px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:5px;white-space:nowrap}
.btn-export:hover{background:#e8edf5}
.btn-export:disabled{opacity:.55;cursor:not-allowed}
.export-menu{position:absolute;top:calc(100% + 6px);right:0;background:#fff;border:1.5px solid #e4eaf2;border-radius:10px;box-shadow:0 6px 20px rgba(0,0,0,.1);z-index:200;min-width:160px;overflow:hidden}
.export-opt{display:flex;align-items:center;width:100%;padding:10px 14px;background:none;border:none;font-size:13px;color:#1a2b45;cursor:pointer;text-align:left}
.export-opt:hover{background:#f4f7fb}
.btn-apply{background:#004AAD;color:#fff;border:none;padding:9px 14px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-apply:hover{background:#003b8e}
.btn-clear{background:#f4f7fb;color:#3d5068;border:1.5px solid #e4eaf2;padding:9px 14px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-clear:hover{background:#e8edf5}
.me-2{margin-right:6px}
.col-picker-backdrop{position:fixed;inset:0;background:rgba(15,25,40,.45);z-index:1060;display:flex;align-items:center;justify-content:center;padding:20px}
.col-picker-modal{background:#fff;border-radius:14px;width:100%;max-width:480px;box-shadow:0 24px 60px rgba(0,0,0,.18);overflow:hidden}
.col-picker-header{display:flex;align-items:flex-start;justify-content:space-between;padding:20px 22px 14px;border-bottom:1px solid #f0f4f9}
.col-picker-title{font-size:15px;font-weight:700;color:#1a2b45;margin:0 0 3px}
.col-picker-sub{font-size:12px;color:#7a90b0;margin:0}
.col-picker-close{background:none;border:none;color:#7a90b0;font-size:15px;cursor:pointer;padding:4px;line-height:1}
.col-picker-close:hover{color:#1a2b45}
.col-picker-actions{display:flex;align-items:center;gap:8px;padding:10px 22px;background:#f8fafc;font-size:12px;border-bottom:1px solid #f0f4f9}
.col-picker-link{background:none;border:none;color:#004AAD;font-size:12px;cursor:pointer;padding:0;font-weight:600;font-family:inherit}
.col-picker-link:hover{text-decoration:underline}
.col-picker-sep{color:#c5d3e0}
.col-picker-count{margin-left:auto;color:#7a90b0;font-size:11.5px}
.col-picker-grid{display:grid;grid-template-columns:1fr 1fr;gap:2px;padding:14px 16px;max-height:280px;overflow-y:auto}
.col-picker-item{display:flex;align-items:center;gap:9px;padding:9px 10px;border-radius:8px;cursor:pointer;transition:background .1s}
.col-picker-item:hover{background:#f4f7fb}
.col-picker-check{width:15px;height:15px;accent-color:#004AAD;cursor:pointer;flex-shrink:0}
.col-picker-label{font-size:13px;color:#1a2b45;user-select:none}
.col-picker-footer{display:flex;justify-content:flex-end;gap:8px;padding:14px 20px;border-top:1px solid #f0f4f9}
.fade-enter-active,.fade-leave-active{transition:opacity .15s ease}
.fade-enter,.fade-leave-to{opacity:0}
</style>
<style src="@vueform/multiselect/themes/default.css"></style>
<style>
.ms-convert {
  --ms-border-color: #dde5f0;
  --ms-border-width: 1.5px;
  --ms-radius: 8px;
  --ms-font-size: 13px;
  --ms-py: 0.45rem;
  --ms-px: 0.75rem;
  --ms-ring-color: rgba(0,74,173,.07);
  --ms-ring-width: 3px;
  --ms-option-bg-selected: #004AAD;
  --ms-option-bg-selected-pointed: #003b8e;
  --ms-option-color-selected: #fff;
  --ms-dropdown-border-color: #dde5f0;
  --ms-dropdown-radius: 8px;
}
</style>
